import { useState } from 'react';
import { CmsSectionShell } from './CmsSectionShell.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf } from './cmsTheme.ts';
import { ImageLightbox } from '../ImageLightbox.tsx';
import { cdnSrcSet, cdnVariant } from '../../lib/cdn.ts';
import type { PhotoWallSection } from '../../lib/types.ts';
import { isLarge } from './photoWallLayout.ts';

/**
 * A whole photo wall shown as a mosaic — every image the wall holds, not a hand-picked few, which
 * is what separates it from `CmsGallerySection`'s carousel.
 *
 * The layout leans on one trick: the grid is twelve *square* cells wide, so a tile's aspect ratio
 * is exactly its column span over its row span (the gaps scale identically in both directions and
 * cancel out). A standard tile spans 3x2 cells and a large one 6x4 — both precisely 3:2, so both
 * ask for the same `/3x2/` rendition the media library already generates, just at different widths.
 * No new rendition family, no focus-point guesswork: the images are uploaded already cropped to
 * 3:2, so the rendition only scales them down.
 *
 * Square cells come from container query units rather than JavaScript: `100cqw` is the wall's own
 * width, so a row's height can be derived from the column width without measuring anything.
 */
export function CmsPhotoWallSection({ theme, guitar, heading, images }: PhotoWallSection) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const items = images ?? [];
  if (items.length === 0) return null;

  const t = themeOf(theme);
  const open = openIndex === null ? null : items[openIndex];

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      {heading && <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>{heading}</SectionHeading>}

      <div className="mf-photowall-wrap">
        <div className="mf-photowall">
          {items.map((image, i) => (
            <button
              key={`${image.src}-${i}`}
              type="button"
              onClick={() => setOpenIndex(i)}
              aria-label={image.alt || `Billede ${i + 1}`}
              className={`group relative block w-full h-full overflow-hidden ${t.img} cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-mf-coral ${
                isLarge(i, items.length) ? 'mf-photowall-lg rounded-card' : 'mf-photowall-sm rounded-card'
              }`}
            >
              <img
                src={cdnVariant(image.src, isLarge(i, items.length) ? 'tablet' : 'mobile') ?? image.src}
                srcSet={cdnSrcSet(image.src) ?? undefined}
                sizes={isLarge(i, items.length) ? '(min-width: 640px) 50vw, 100vw' : '(min-width: 640px) 25vw, 50vw'}
                alt={image.alt}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <span className="absolute inset-0 bg-linear-to-t from-mf-black/30 to-transparent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
            </button>
          ))}
        </div>
      </div>

      {open && (
        <ImageLightbox
          image={open}
          counter={`${openIndex! + 1} af ${items.length}`}
          onClose={() => setOpenIndex(null)}
          onPrev={() => setOpenIndex(((openIndex! - 1) + items.length) % items.length)}
          onNext={() => setOpenIndex((openIndex! + 1) % items.length)}
        />
      )}
    </CmsSectionShell>
  );
}

